import { applyDocumentLinePatch, toNumber } from '../shared/balanceUtils';
import type { DocumentLineForm } from '../shared/documentLineTypes';

export const round2 = (n: number) => Math.round(n * 100) / 100;

/** Unique key for a payment invoice row — same link can cover multiple invoices. */
export const invoiceLineKey = (line: Pick<DocumentLineForm, 'linkNumber' | 'narrative' | 'key'>) => {
  const link = line.linkNumber?.trim();
  if (!link) return `row:${line.key}`;
  const narrative = (line.narrative || '').trim().toLowerCase();
  return narrative ? `link:${link}|${narrative}` : `link:${link}`;
};

export const lineMaxPay = (line: DocumentLineForm): number => {
  const fromOpen = toNumber(line.openBalance);
  if (fromOpen > 0.005) return round2(fromOpen);
  const fromCredit = toNumber(line.creditLocal);
  if (fromCredit > 0.005) return round2(fromCredit);
  return 0;
};

export const isPartialPayLine = (line: DocumentLineForm): boolean => {
  const pay = toNumber(line.creditLocal);
  const max = lineMaxPay(line);
  return pay > 0.005 && max > 0.005 && pay < max - 0.01;
};

export const isOverPayLine = (line: DocumentLineForm): boolean => {
  const pay = toNumber(line.creditLocal);
  const max = lineMaxPay(line);
  return max > 0.005 && pay > max + 0.01;
};

export type PaySettlementStatus = 'full' | 'partial' | 'skipped' | 'pending';

/** How this payment line settles the open item (before save). */
export function resolvePaySettlementStatus(line: DocumentLineForm): PaySettlementStatus {
  const pay = toNumber(line.creditLocal);
  const max = lineMaxPay(line);
  if (pay <= 0.005) return 'skipped';
  if (max <= 0.005) return 'pending';
  if (pay >= max - 0.01) return 'full';
  return 'partial';
}

export const remainingAfterPay = (line: DocumentLineForm): number => {
  const max = lineMaxPay(line);
  const pay = toNumber(line.creditLocal);
  if (max <= 0.005) return 0;
  return round2(Math.max(0, max - pay));
};

export const settlementStatusLabel: Record<PaySettlementStatus, string> = {
  full: 'Full pay',
  partial: 'Partial',
  skipped: 'Not paying',
  pending: '—',
};

/** Distribute bank amount across invoice lines — oldest due date first, last invoice may be partial. */
export function allocateBankAmountToLines(
  lines: DocumentLineForm[],
  bankAmount: number,
): DocumentLineForm[] {
  const total = round2(bankAmount);
  if (total <= 0.005 || lines.length === 0) return lines;

  const sorted = [...lines].sort((a, b) => {
    const dueA = a.dueDate?.trim() || '9999-12-31';
    const dueB = b.dueDate?.trim() || '9999-12-31';
    const dueCmp = dueA.localeCompare(dueB);
    if (dueCmp !== 0) return dueCmp;
    return (a.linkNumber || a.narrative).localeCompare(b.linkNumber || b.narrative);
  });

  let remaining = total;
  const payByKey = new Map<string, number>();

  for (const line of sorted) {
    if (remaining <= 0.005) {
      payByKey.set(line.key, 0);
      continue;
    }
    const max = lineMaxPay(line);
    if (max <= 0.005) {
      payByKey.set(line.key, 0);
      continue;
    }
    const pay = round2(Math.min(remaining, max));
    payByKey.set(line.key, pay);
    remaining = round2(remaining - pay);
  }

  return lines.map((line) => {
    const pay = payByKey.get(line.key) ?? 0;
    if (pay <= 0.005) {
      return applyDocumentLinePatch(line, { creditLocal: '', creditUSD: '' });
    }
    return applyDocumentLinePatch(line, { creditLocal: String(pay) });
  });
}

type LinkPickerRecord = {
  linkNumber: string | null;
  linkBalance: number | null;
  debitLocal: number;
};

type LinkPickerResponse = {
  records?: LinkPickerRecord[];
  openLinks?: { linkNumber: string; balance: number }[];
};

/** Build link → open balance map from link-picker API response. */
export function openBalanceByLinkFromPicker(data: LinkPickerResponse): Map<string, number> {
  const map = new Map<string, number>();
  for (const link of data.openLinks ?? []) {
    if (link.balance > 0.005) map.set(link.linkNumber, round2(link.balance));
  }
  for (const rec of data.records ?? []) {
    const link = rec.linkNumber?.trim();
    if (!link) continue;
    const balance = rec.linkBalance ?? (rec.debitLocal > 0.005 ? rec.debitLocal : null);
    if (balance != null && balance > 0.005) {
      map.set(link, round2(balance));
    }
  }
  return map;
}

/** Max pay for an edited draft line = current open balance + amount already on this draft line. */
export function refreshLineOpenBalances(
  lines: DocumentLineForm[],
  linkBalances: Map<string, number>,
): DocumentLineForm[] {
  return lines.map((line) => {
    const link = line.linkNumber?.trim();
    if (!link) {
      const fallback = lineMaxPay(line);
      return fallback > 0.005 ? { ...line, openBalance: String(fallback) } : line;
    }
    const open = linkBalances.get(link) ?? 0;
    const maxPay = round2(open + toNumber(line.creditLocal));
    return maxPay > 0.005 ? { ...line, openBalance: String(maxPay) } : line;
  });
}

type PickerDebitRow = {
  linkNumber: string | null;
  narrative: string;
  debitLocal: number;
  linkBalance: number | null;
};

/**
 * Restore per-invoice outstanding from link-picker charge lines (match by narrative).
 * Avoids treating shared link balance as every row's outstanding.
 */
export function refreshPaymentInvoiceOpenBalances(
  lines: DocumentLineForm[],
  pickerRows: PickerDebitRow[],
): DocumentLineForm[] {
  const usedRecordIndexes = new Set<number>();

  return lines.map((line) => {
    const narrative = (line.narrative || '').trim().toLowerCase();
    const link = line.linkNumber?.trim();
    const credit = toNumber(line.creditLocal);

    let matchIdx = -1;
    if (narrative) {
      matchIdx = pickerRows.findIndex((r, idx) => {
        if (usedRecordIndexes.has(idx)) return false;
        const rNar = (r.narrative || '').trim().toLowerCase();
        if (rNar !== narrative) return false;
        if (link && r.linkNumber?.trim() && r.linkNumber.trim() !== link) return false;
        return true;
      });
    }

    if (matchIdx < 0 && link) {
      // Fall back: exclusive link match only when a single unused charge shares the link
      const candidates = pickerRows
        .map((r, idx) => ({ r, idx }))
        .filter(({ r, idx }) => !usedRecordIndexes.has(idx) && r.linkNumber?.trim() === link);
      if (candidates.length === 1) matchIdx = candidates[0].idx;
    }

    if (matchIdx >= 0) {
      usedRecordIndexes.add(matchIdx);
      const match = pickerRows[matchIdx];
      const invoiceOpen = match.debitLocal > 0.005
        ? round2(match.debitLocal)
        : (match.linkBalance != null && match.linkBalance > 0.005 ? round2(match.linkBalance) : 0);
      if (invoiceOpen > 0.005) {
        return { ...line, openBalance: invoiceOpen.toFixed(2) };
      }
    }

    if (credit > 0.005) {
      // Keep prior open if it already exceeds pay (partial); else treat as full
      const existing = toNumber(line.openBalance);
      if (existing > credit + 0.005) return line;
      return { ...line, openBalance: credit.toFixed(2) };
    }
    return line;
  });
}
